/* =========================================================================
   Pellington - PDP gallery: progress bar + in-place zoom. 8 Sep 2026.

   Three jobs: pin the bar and the button to the IMAGE, move the bar fill,
   and run the zoom. The swipe is the theme's own native scroll-snap track
   and is not touched.

   No requestAnimationFrame anywhere. The card build shipped an rAF-throttled
   bar that measured correct and never ran while document.hidden was true,
   because a hidden tab is served no frames. Same rule here.
   ========================================================================= */
(function () {
  'use strict';

  var ZOOM = 2.4;
  window.__pellPdpGallery = true;

  function each(list, fn) { for (var i = 0; i < list.length; i++) fn(list[i], i); }
  function trackOf(gal) { return gal.querySelector('.slider--generic'); }

  /* Which slide is showing, by geometry: the slide overlapping the track's
     own box the most. Deliberately NOT scrollLeft - its sign flips between
     engines in RTL. Bounding boxes read the same in both directions. */
  function activeIndex(track) {
    var tr = track.getBoundingClientRect();
    var kids = track.children, best = 0, bestOverlap = -1;
    for (var i = 0; i < kids.length; i++) {
      var r = kids[i].getBoundingClientRect();
      var o = Math.min(r.right, tr.right) - Math.max(r.left, tr.left);
      if (o > bestOverlap) { bestOverlap = o; best = i; }
    }
    return best;
  }

  function activeSlide(gal) {
    var track = trackOf(gal);
    if (!track) return null;
    return track.children[activeIndex(track)] || null;
  }

  function activeMedia(gal) {
    var slide = activeSlide(gal);
    return slide ? slide.querySelector('img, video, model-viewer') : null;
  }

  /* ---- pin the furniture to the picture, not to the slide box ----------
     The track is not the photograph. .slideshow__slide is a flex box that
     centres the image both ways, and with image_aspect_ratio 'adapt' it can
     be far taller than the picture inside it - measured on the draft at
     1280px: an 840px slide around a 589px image. A bar anchored to the slide
     therefore sat 125px BELOW the photograph, which is the gap the owner
     reported on 8 Sep. On a phone the image fills the box, all three offsets
     are 0, and nothing moves - which is why it only ever showed on desktop.

     There is no wrapper hugging the image to anchor to in CSS: the <img> is
     a bare flex child of the slide. So it is measured.

     Physical left/right/bottom here, deliberately: these are measured pixel
     gaps, not reading direction. The FILL inside the bar keeps logical
     properties, which is the part that has to flip for Arabic.

     Returns a signature of what it wrote, so settle() can tell when the
     layout has stopped moving.

     ⚑ IT REFUSES TO MEASURE WHILE ZOOMED. A zoomed image reports a painted
     box 2.4x its own size, so tr.bottom - ir.bottom goes negative, clamps to
     0, and the zoom button drops 125px to the bottom of the letterbox in
     front of the shopper. The bar is invisible at that moment but the button
     is not. The sentinel below is deliberately a constant, so a settle()
     poll running when the shopper hits zoom reads it as "stable" and retires
     instead of spinning to its ceiling. */
  function frame(gal) {
    if (gal.classList.contains('pell-gal--zoomed')) return 'zoomed';
    var track = trackOf(gal);
    var media = activeMedia(gal);
    if (!track || !media) return null;
    var tr = track.getBoundingClientRect();
    var ir = media.getBoundingClientRect();
    if (!ir.height) return null;
    var off = Math.max(0, Math.round(tr.bottom - ir.bottom));
    var insetL = Math.max(0, Math.round(ir.left - tr.left));
    var insetR = Math.max(0, Math.round(tr.right - ir.right));
    gal.style.setProperty('--pell-gal-offset', off + 'px');
    gal.style.setProperty('--pell-gal-inset-l', insetL + 'px');
    gal.style.setProperty('--pell-gal-inset-r', insetR + 'px');
    return off + '|' + insetL + '|' + insetR;
  }

  /* ⚑ THE MEASUREMENT HAS TO BE REPEATED UNTIL IT STOPS CHANGING, AND A
     FIXED LADDER OF TIMEOUTS IS NOT GOOD ENOUGH. This is the third attempt
     at this one function and the reasoning is worth keeping.

     The image carries an aspect ratio, so its height follows its WIDTH - and
     its width goes on changing as the page resolves: fonts land, the sticky
     column settles, a scrollbar appears and takes 8px off the measure. Every
     reading taken before that lands is wrong, and each fixed schedule landed
     on a different wrong number: 57px at build, 110px after a 400ms timer,
     117px after a five-rung ladder ending at 2s, against a true 125px.

     A ResizeObserver does not rescue it either, and the reason is exact:
     RO reports SIZE, and this offset depends on POSITION. When a scrollbar
     narrows the column the image resizes and RO fires; when something above
     the gallery reflows, the image only MOVES, RO stays silent, and the bar
     is left stranded. Verified on the draft: stable geometry at 125px with
     the stored offset still reading 117px, hours after load.

     So the poll below stops when the answer stops changing rather than when
     a clock runs out: four identical readings ~700ms apart, or an 8 second
     ceiling, whichever comes first. Two getBoundingClientRect calls per tick,
     at most 45 ticks, then it is gone. Not elegant. Correct, bounded, and
     honest about the fact that layout settling has no reliable event. */
  function settle(gal) {
    if (gal.__pellSettle) { clearInterval(gal.__pellSettle); gal.__pellSettle = 0; }
    var last = frame(gal), same = 0, n = 0;
    gal.__pellSettle = setInterval(function () {
      var v = frame(gal);
      n++;
      if (v !== null && v === last) { same++; } else { same = 0; last = v; }
      if (same >= 4 || n >= 45) { clearInterval(gal.__pellSettle); gal.__pellSettle = 0; }
    }, 180);
  }

  function updateBar(track) {
    var gal = track.closest ? track.closest('.slideshow') : null;
    if (!gal) return;
    var bar = gal.querySelector('.pell-gal-bar');
    if (bar) bar.style.setProperty('--pell-gal-i', activeIndex(track));
    frame(gal);
  }

  /* ---- build: one bar and one button per gallery ----------------------- */
  function build(gal) {
    if (gal.__pellGal) return;
    gal.__pellGal = true;

    var track = trackOf(gal);
    if (!track) return;
    var count = track.children.length;

    var bar = document.createElement('div');
    bar.className = 'pell-gal-bar';
    bar.setAttribute('aria-hidden', 'true');
    bar.style.setProperty('--pell-gal-n', count);
    bar.style.setProperty('--pell-gal-i', activeIndex(track));
    if (count < 2) bar.setAttribute('data-single', '');
    bar.appendChild(document.createElement('i'));
    gal.appendChild(bar);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pell-gal-zoom';
    btn.setAttribute('aria-label', 'Zoom image');
    btn.innerHTML =
      '<svg class="pell-gal-zoom__in" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M8 3H3v5M12 17h5v-5M3 3l6 6M17 17l-6-6"/></svg>' +
      '<svg class="pell-gal-zoom__out" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M3 8h5V3M17 12h-5v5M9 9L3 3M11 11l6 6"/></svg>';
    btn.addEventListener('click', function (e) { e.preventDefault(); toggle(gal); });
    gal.appendChild(btn);

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { frame(gal); });
      ro.observe(track);
      each(gal.querySelectorAll('img'), function (img) { ro.observe(img); });
    }
    each(gal.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', function () { settle(gal); }, { once: true });
    });
    window.addEventListener('load', function () { settle(gal); });
    settle(gal);
  }

  /* ---- clip the magnification to the picture's own rectangle ------------
     Reported 8 Sep, desktop only: "clicking the zoom is changing the image
     size which isn't the original design." Correct, and not an illusion.
     scale(2.4) on a 589px image inside an 840px slide paints a 1413px image
     that only the 840px track clips, so the picture spilled 125px above and
     below into its own letterbox and read as the frame growing.

     Measured from LAYOUT boxes - offsetWidth/offsetHeight on the image,
     clientWidth/clientHeight on the slide - because those ignore transforms.
     The same call is therefore correct whether the image is currently zoomed
     or not, which is what lets a resize mid-zoom recompute without having to
     unzoom first. Halved because the slide centres its child on both axes;
     computed styles on the draft: justify-content:center, align-items:center,
     padding 0. Rect-measured gaps agreed to within the half-pixel. */
  function clip(slide) {
    var media = slide.querySelector('img, video, model-viewer');
    if (!media) return;
    var y = Math.max(0, (slide.clientHeight - media.offsetHeight) / 2);
    var x = Math.max(0, (slide.clientWidth - media.offsetWidth) / 2);
    slide.style.setProperty('--pell-clip-y', y + 'px');
    slide.style.setProperty('--pell-clip-x', x + 'px');
    slide.classList.add('pell-gal-clip');
  }

  function unclip(slide) {
    slide.classList.remove('pell-gal-clip');
    slide.style.removeProperty('--pell-clip-y');
    slide.style.removeProperty('--pell-clip-x');
  }

  /* ---- zoom ------------------------------------------------------------ */
  function toggle(gal) {
    var slide = activeSlide(gal);
    var media = slide ? slide.querySelector('img, video, model-viewer') : null;
    if (!media) return;
    var on = gal.classList.toggle('pell-gal--zoomed');
    var btn = gal.querySelector('.pell-gal-zoom');
    if (on) {
      clip(slide);
      media.classList.add('pell-gal-slide-media');
      media.setAttribute('draggable', 'false');
      media.style.setProperty('--pell-z', ZOOM);
      media.style.setProperty('--pell-zx', '0px');
      media.style.setProperty('--pell-zy', '0px');
      if (btn) btn.setAttribute('aria-label', 'Exit zoom');
    } else {
      media.style.setProperty('--pell-z', 1);
      media.style.setProperty('--pell-zx', '0px');
      media.style.setProperty('--pell-zy', '0px');
      media.classList.remove('pell-gal-slide-media');
      media.removeAttribute('draggable');
      unclip(slide);
      if (btn) btn.setAttribute('aria-label', 'Zoom image');
      /* the image animates back over .25s and frame() was refusing to
         measure until a moment ago, so re-measure across the return. */
      settle(gal);
    }
  }

  /* Drag to pan, clamped so the image can never be dragged off its own
     frame. Pointer events, so mouse and finger are one code path. */
  var drag = null;

  document.addEventListener('pointerdown', function (e) {
    var media = e.target.closest ? e.target.closest('.pell-gal-slide-media') : null;
    if (!media) return;
    var gal = media.closest('.slideshow');
    if (!gal || !gal.classList.contains('pell-gal--zoomed')) return;

    /* ⚑ MOUSE ONLY, AND THE GATE IS THE POINT. A mouse press on an <img>
       starts the browser's native image drag, which steals the pointer and
       fires pointercancel - the measured cause of "moving the image inside
       the zoom is not happening" on desktop while mobile was perfect.
       preventDefault here kills that. It is gated to pointerType 'mouse'
       because on touch a prevented pointerdown can also kill scrolling, and
       touch already works: there is nothing to fix and everything to lose. */
    if (e.pointerType === 'mouse') e.preventDefault();

    /* ⚑ THE CLAMP MEASURES THE LAYOUT BOX, NOT THE PAINTED ONE, AND THE
       DIFFERENCE IS A FACTOR OF 2.4.

       The transform is scale(Z) translate(tx,ty) - translate written AFTER
       scale, so the browser scales the translation too and the picture moves
       Z*tx on screen. The image overhangs its frame by W*(Z-1)/2 each side,
       so the honest limit is tx <= W*(Z-1)/(2Z), with W the image's OWN width.

       getBoundingClientRect() here returns the box after the transform -
       1413px, not 589px - so reading W from it inflated the limit to 412px
       against a true 171.8px and let the image be dragged clean off its own
       frame: measured on the draft, a 132px empty band down the left edge
       and 131px across the top. offsetWidth/offsetHeight are layout values
       and ignore transforms, which is exactly what this needs. */
    drag = {
      media: media,
      x: e.clientX, y: e.clientY,
      ox: parseFloat(media.style.getPropertyValue('--pell-zx')) || 0,
      oy: parseFloat(media.style.getPropertyValue('--pell-zy')) || 0,
      maxX: media.offsetWidth * (ZOOM - 1) / (2 * ZOOM),
      maxY: media.offsetHeight * (ZOOM - 1) / (2 * ZOOM)
    };
    if (media.setPointerCapture) { try { media.setPointerCapture(e.pointerId); } catch (err) {} }
  });

  /* Second belt: if any engine still starts a drag despite the above, it is
     refused here before it can take the pointer. */
  document.addEventListener('dragstart', function (e) {
    var media = e.target.closest ? e.target.closest('.pell-gal-slide-media') : null;
    if (media) e.preventDefault();
  });

  document.addEventListener('pointermove', function (e) {
    if (!drag) return;
    var nx = drag.ox + (e.clientX - drag.x) / ZOOM;
    var ny = drag.oy + (e.clientY - drag.y) / ZOOM;
    nx = Math.max(-drag.maxX, Math.min(drag.maxX, nx));
    ny = Math.max(-drag.maxY, Math.min(drag.maxY, ny));
    drag.media.style.setProperty('--pell-zx', nx + 'px');
    drag.media.style.setProperty('--pell-zy', ny + 'px');
  });

  function endDrag() { drag = null; }
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  /* ---- wiring ---------------------------------------------------------- */
  document.addEventListener('scroll', function (e) {
    var t = e.target;
    if (!t || t.nodeType !== 1 || !t.classList) return;
    if (!t.classList.contains('slider--generic')) return;
    if (!t.closest('.pdp--media-slideshow')) return;
    updateBar(t);
  }, true);

  function init() { each(document.querySelectorAll('.pdp--media-slideshow .slideshow'), build); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }

  window.addEventListener('resize', function () {
    each(document.querySelectorAll('.pdp--media-slideshow .slideshow'), function (gal) {
      each(gal.querySelectorAll('.pell-gal-clip'), clip);
      settle(gal);
    });
    each(document.querySelectorAll('.pdp--media-slideshow .slider--generic'), updateBar);
  }, { passive: true });
})();
